import React, { useState } from "react";
import "./dateAndTimePicker.css"
import { DatePicker, ConfigProvider } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import Cookies from "universal-cookie";
import CustomIcon from "../../Components/CustomIcon";

dayjs.extend(utc);
dayjs.extend(timezone);

const { RangePicker } = DatePicker;

/**
 * @param {Array|null}   defaultValue   - [dayjsStart, dayjsEnd]
 * @param {Function}     handleOnChange - ({ startDate, endDate }) in "YYYY-MM-DD HH:mm:ss"
 * @param {dayjs|null}   minDateTime    - earliest selectable datetime (inclusive)
 * @param {dayjs|null}   maxDateTime    - latest selectable datetime (inclusive), defaults to now
 * @param {number|null}  maxRangeHours      - maximum allowed hours between start and end
 * @param {Function}     onMaxRangeExceeded - called with { adjustedEndDate } when range is clamped
 */
const AntDateAndTimePicker = ({
  defaultValue = null,
  handleOnChange,
  minDateTime = null,
  maxDateTime = null,
  maxRangeHours = null,
  onMaxRangeExceeded = null,
}) => {
  const cookies = new Cookies();
  const userTimezone = cookies.get("timeZone") || "UTC";

  const [value, setValue] = useState(
    defaultValue && defaultValue.length === 2
      ? [
          dayjs(defaultValue[0]).tz(userTimezone),
          dayjs(defaultValue[1]).tz(userTimezone),
        ]
      : null
  );

  const [isOpen, setIsOpen] = useState(false);
  const now = dayjs().tz(userTimezone);
  const effectiveMax = maxDateTime || now;

  // Build presets filtered to respect min/max bounds
  const allPresets = [
    { label: "Within 12 hours", value: [now.subtract(12, "hour"), now] },
    { label: "12 to 24 hours", value: [now.subtract(24, "hour"), now.subtract(12, "hour")] },
    { label: "1 day to 2 days", value: [now.subtract(2, "day"), now.subtract(1, "day")] },
    { label: "2 days to 7 days", value: [now.subtract(7, "day"), now.subtract(2, "day")] },
    { label: "7 days to 30 days", value: [now.subtract(30, "day"), now.subtract(7, "day")] },
    { label: "Greater than 30 days", value: [now.subtract(60, "day"), now.subtract(30, "day")] },
  ];

  const presets = allPresets.filter(({ value: [start, end] }) => {
    if (minDateTime && end.isBefore(minDateTime)) return false;
    if (end.isAfter(effectiveMax)) return false;
    if (maxRangeHours && end.diff(start, "hour", true) > maxRangeHours) return false;
    return true;
  });

  const normalizeRange = (dates) => {
    if (!dates || dates.length !== 2) return null;
    const [d1, d2] = dates;
    if (!d1 || !d2) return dates;
    return d1.isBefore(d2) ? [d1, d2] : [d2, d1];
  };

  const onChange = (dates) => {
    if (!dates) {
      setValue(null);
      handleOnChange?.(null);
      return;
    }

    const normalized = normalizeRange(dates);
    if (!normalized) return;

    const [start, end] = normalized;

    // Enforce maxRangeHours
    if (maxRangeHours && end.diff(start, "hour", true) > maxRangeHours) {
      const clamped = [start, start.add(maxRangeHours, "hour")];
      setValue(clamped);
      handleOnChange?.({
        startDate: clamped[0].format("YYYY-MM-DD HH:mm:ss"),
        endDate: clamped[1].format("YYYY-MM-DD HH:mm:ss"),
      });
      onMaxRangeExceeded?.({ adjustedEndDate: clamped[1].format("YYYY-MM-DD HH:mm:ss") });
      return;
    }

    setValue(normalized);
    handleOnChange?.({
      startDate: start.format("YYYY-MM-DD HH:mm:ss"),
      endDate: end.format("YYYY-MM-DD HH:mm:ss"),
    });
  };

  // Disable dates outside [minDateTime, maxDateTime]
  const disabledDate = (current) => {
    if (!current) return false;
    if (minDateTime && current.isBefore(minDateTime, "day")) return true;
    if (current.isAfter(effectiveMax, "day")) return true;
    return false;
  };

  // Disable hours/minutes outside allowed range for a given panel side
  const disabledTime = (date, type) => {
    if (!date) return {};
    const isStart = type === "start";

    // Disable future times (after effectiveMax)
    if (date.isSame(effectiveMax, "day")) {
      const maxHour = effectiveMax.hour();
      const maxMinute = effectiveMax.minute();
      return {
        disabledHours: () => Array.from({ length: 24 - maxHour - 1 }, (_, i) => maxHour + 1 + i),
        disabledMinutes: (h) => h === maxHour
          ? Array.from({ length: 60 - maxMinute - 1 }, (_, i) => maxMinute + 1 + i)
          : [],
      };
    }

    // Disable times before minDateTime on the minDateTime date
    if (minDateTime && date.isSame(minDateTime, "day") && isStart) {
      const minHour = minDateTime.hour();
      const minMinute = minDateTime.minute();
      return {
        disabledHours: () => Array.from({ length: minHour }, (_, i) => i),
        disabledMinutes: (h) => h === minHour
          ? Array.from({ length: minMinute }, (_, i) => i)
          : [],
      };
    }

    return {};
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#4F44E0",
          borderRadius: 4,
          controlHeight: 32,
        },
      }}
    >
      <div style={{ width: "fit-content" }}>
        <RangePicker
          showNow={false}
          showTime={{ showNow: false }}
          value={value}
          onChange={onChange}
          presets={presets}
          format="YYYY/MM/DD hh:mm A"
          placeholder={["Start date & time", "End date & time"]}
          disabledDate={disabledDate}
          disabledTime={disabledTime}
          onOpenChange={(open) => setIsOpen(open)}
          style={{ width: "420px" }}
          separator={<span style={{ margin: "0 8px" }}>→</span>}
          getPopupContainer={() => document.body}
          popupStyle={{ zIndex: 2000 }}
          suffixIcon={
            <CustomIcon
              name="calendar"
              sx={{ fontSize: "16px", cursor: "pointer" }}
              stroke={isOpen ? "#6D72F6" : "#444B51"}
            />
          }
        />
      </div>
    </ConfigProvider>
  );
};

export default AntDateAndTimePicker;
